import { KeyBinding } from '../../../shared/CharacterTypes';

/**
 * Representation of a DOM key code and optional modifier bound to an action.
 */
export interface BoundActionCode {
  code: string;
  modifier?: string;
}

/**
 * Map of action name to array of bound DOM codes and their modifier requirements.
 */
export type ActionBindingMap = Map<string, BoundActionCode[]>;

/**
 * Translates Phaser/Config key names to DOM Event 'code' values.
 * Returns null if the key is unknown or explicitly unmapped (e.g. BACKTICK).
 */
export function mapPhaserKeyToDOMCode(phaserKey: string): string | null {
  const upper = phaserKey.toUpperCase();

  // Letters (A-Z -> KeyA-KeyZ)
  if (upper.length === 1 && upper >= 'A' && upper <= 'Z') return `Key${upper}`;
  // Numbers (0-9 -> Digit0-Digit9)
  if (upper.length === 1 && upper >= '0' && upper <= '9') return `Digit${upper}`;

  const map: Record<string, string> = {
    'SPACE': 'Space',
    'ENTER': 'Enter',
    'SHIFT': 'ShiftLeft', // Default to left
    'CTRL': 'ControlLeft',
    'ALT': 'AltLeft',
    'ALTLEFT': 'AltLeft',
    'ALT_LEFT': 'AltLeft',
    'ALTRIGHT': 'AltRight',
    'ALT_RIGHT': 'AltRight',
    'UP': 'ArrowUp',
    'DOWN': 'ArrowDown',
    'LEFT': 'ArrowLeft',
    'RIGHT': 'ArrowRight',
    'TAB': 'Tab',
    'ESCAPE': 'Escape',
    'NUMLOCK': 'NumLock',
    // 'BACKTICK' intentionally NOT mapped (2026-06-10) — backtick crashed the
    // client; refusing to resolve it here means even a persisted binding to
    // "BACKTICK" can never produce a DOM code to match against.
    'ONE': 'Digit1',
    'TWO': 'Digit2',
    'THREE': 'Digit3',
    'FOUR': 'Digit4',
    'FIVE': 'Digit5',
    'SIX': 'Digit6',
    'SEVEN': 'Digit7',
    'EIGHT': 'Digit8',
    'NINE': 'Digit9',
    'ZERO': 'Digit0',
    'NUMPAD_ONE': 'Numpad1',
    'NUMPAD_TWO': 'Numpad2',
    'NUMPAD_THREE': 'Numpad3',
    'NUMPAD_FOUR': 'Numpad4',
    'NUMPAD_FIVE': 'Numpad5',
    'NUMPAD_SIX': 'Numpad6',
    'NUMPAD_SEVEN': 'Numpad7',
    'NUMPAD_EIGHT': 'Numpad8',
    'NUMPAD_NINE': 'Numpad9',
    'NUMPAD_ZERO': 'Numpad0',
  };

  return map[upper] || null;
}

/**
 * Compiles a list of KeyBinding records into an action lookup table.
 * Preserves the order of bindings for each action.
 */
export function compileActionBindingMap(bindings: readonly KeyBinding[]): ActionBindingMap {
  const actionToCodes: ActionBindingMap = new Map();

  bindings.forEach(binding => {
    const domCode = mapPhaserKeyToDOMCode(binding.key);
    if (!domCode) return;

    let list = actionToCodes.get(binding.action);
    if (!list) {
      list = [];
      actionToCodes.set(binding.action, list);
    }
    list.push({
      code: domCode,
      modifier: binding.modifier,
    });
  });

  return actionToCodes;
}
